import React from 'react';
import { X, Filter } from 'lucide-react';
import { POrderStatus } from '../../../../../types/purchaseOrderLines';

interface FilterPanelProps {
  filters: {
    itemNo: string;
    supplierNo: string;
    fromDate: string;
    toDate: string;
    maxOrderStatus: number | undefined;
  };
  onFiltersChange: (filters: any) => void;
  onApply: () => void;
  onClear: () => void;
  onClose: () => void;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onFiltersChange,
  onApply,
  onClear,
  onClose,
}) => {
  const handleChange = (field: string, value: any) => {
    onFiltersChange({
      ...filters,
      [field]: value,
    });
  };

  return (
    <div className="mt-4 pt-4 border-t border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-medium text-gray-900">Filtrer resultater</h3>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        {/* Item Number Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Varenummer
          </label>
          <input
            type="text"
            value={filters.itemNo}
            onChange={(e) => handleChange('itemNo', e.target.value)}
            placeholder="Søk etter varenummer..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Supplier Number Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Leverandørnummer
          </label>
          <input
            type="text"
            value={filters.supplierNo}
            onChange={(e) => handleChange('supplierNo', e.target.value)}
            placeholder="Søk etter leverandør..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Max Order Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Maks ordrestatus
          </label>
          <select
            value={filters.maxOrderStatus ?? ''}
            onChange={(e) =>
              handleChange(
                'maxOrderStatus',
                e.target.value ? Number(e.target.value) : undefined
              )
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Alle statuser</option>
            <option value={POrderStatus.Ubehandlet}>
              Ubehandlet (0)
            </option>
            <option value={POrderStatus.Godkjent}>
              Godkjent (10)
            </option>
            <option value={POrderStatus.UnderArbeid}>
              Under arbeid (20)
            </option>
            <option value={POrderStatus.BehandletIkkeKomplett}>
              Behandlet, ikke komplett (30)
            </option>
            <option value={POrderStatus.BehandletKomplett}>
              Behandlet, komplett (40)
            </option>
            <option value={POrderStatus.TilPakking}>
              Til pakking (45)
            </option>
            <option value={POrderStatus.KlarForEksport}>
              Klar for eksport (50)
            </option>
            <option value={POrderStatus.Eksportert}>
              Eksportert (60)
            </option>
          </select>
        </div>

        {/* From Date Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Fra dato
          </label>
          <input
            type="date"
            value={filters.fromDate}
            onChange={(e) => handleChange('fromDate', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* To Date Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Til dato
          </label>
          <input
            type="date"
            value={filters.toDate}
            onChange={(e) => handleChange('toDate', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-3">
        <button
          onClick={onApply}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          Bruk filtre
        </button>
        <button
          onClick={onClear}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Nullstill
        </button>
      </div>
    </div>
  );
};
